import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { LenderDelayedLoan } from '../lender-delayed-loans.component';
import { EmiDelay, Loan } from '../../model/loan.model';
import { isEmpty, maxBy, startCase } from 'lodash';
import { EmiStatus } from '../../model/emi-status';
import { PrepaymentStatus } from '../../model/prepayment-status';
import { ConfigService } from '../../service/config.service';
import { MatSort } from '@angular/material/sort';
import * as moment from 'moment';
import { isDate } from 'moment';
import { Portfolio } from '../../model/portfolio.model';
import { LenderReturnCalculator } from '../../service/lender-return-calculator';
import { Emi } from '../../model/emi-schedule.model';
import { LoanRestructureService } from '../../service/loan-restructure.service';
import { animate, state, style, transition, trigger } from '@angular/animations';

@Component({
    selector: 'app-loan-list',
    templateUrl: './loan-list.component.html',
    styleUrls: ['../../../assets/styles/table-styles.scss', './loan-list.component.css'],
    animations: [
        trigger('detailExpand', [
            state('collapsed', style({ height: '0px', minHeight: '0' })),
            state('expanded', style({ height: '*', minHeight: "*" })),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ]

})
export class LoanListComponent implements OnInit {

    displayedColumns = [
        'portfolioName',
        'loanId',
        'investmentInLoan',
        'numberOfEmiReceived',
        'grossCashFlowReceived',
        'numberOfEmisOverdue',
        'totalAmountOverdue',
        'netInterestEarned',
        'outstandingPrincipal',
        'lastPaymentDate'
    ];

    @Input()
    loans: Loan[];

    @Input()
    lenderId: string;

    @Input()
    portfolios: Map<string, Portfolio>;

    dataSource = new MatTableDataSource<LenderDelayedLoan>();

    @ViewChild(MatSort)
    public set matSort(val: MatSort) {
        this.dataSource.sort = val;
    }

    public totalInvestmentInLoan: number;
    public totalGrossCashFlow: number;
    public totalInterestEarned: number;
    public totalOutstandingPrincipal: number;
    public totalDelayedEmiAmount: number;
    networkCallInProgress: boolean;
    private readonly configService: ConfigService;
    private readonly lenderReturnCalculator: LenderReturnCalculator;
    private readonly loanRestructureService: LoanRestructureService;
    expandedElement: LenderDelayedLoan | null;
    public isMobile: boolean = false;
    screenWidth: number;

    constructor(configService: ConfigService, lenderReturnCalculator: LenderReturnCalculator,
        loanRestructureService: LoanRestructureService) {
        this.configService = configService;
        this.lenderReturnCalculator = lenderReturnCalculator;
        this.loanRestructureService = loanRestructureService;
    }
    ngAfterViewInit() {
        this.screenWidth = window.innerWidth;
        this.isMobile = (<any>window).innerWidth < 500;
        if (this.isMobile) {
            this.displayedColumns = ['portfolioName',
                'loanId',
                'investmentInLoan','accordion_arrow'];
        }
    }

    async ngOnInit(): Promise<void> {
        this.networkCallInProgress = true;
        this.dataSource.data = await this.populateLenderLoansData(this.loans, this.lenderId);
        this.networkCallInProgress = false;
    }

    public async populateLenderLoansData(delayedLoans: Loan[], lenderId: string): Promise<LenderDelayedLoan[]> {
        const lenderDelayedLoans: LenderDelayedLoan[] = [];
        if (delayedLoans && delayedLoans.length > 0) {
            for (const loan of delayedLoans) {
                const lenderInvestment = loan.lenders_invested.find(investment => investment.lender_id === lenderId);
                const outStandingDelay = loan.emi_delay_details.emi_delay.find(delay => !delay.is_delay_amount_paid);
                const portfolio = this.portfolios.get(lenderInvestment.portfolio_id);
                const lenderShare = lenderInvestment.invested_amount / loan.loan_fulfilled_amount;
                const totalNetAmountOverDue = this.lenderReturnCalculator.getNetEmiAmountOverdueForLender(loan, lenderId);
                const numberOfEmisOverdue = outStandingDelay ? outStandingDelay.associatedEmis.length : this.getNoOfEmiDelays(loan);
                const numberOfDaysDelayed = this.getNoOfDaysDelayed(outStandingDelay, loan);
                const receivedEmiMetric = await this.getReceivedEmiMetric(loan);
                const netInterestEarned = await this.lenderReturnCalculator.getNetInterestEarnedForLender(loan, lenderId);
                const outstandingPrincipal = this.getOutstandingPrincipal(loan);
                lenderDelayedLoans.push({
                    loanId: loan.generated_loan_id,
                    rating: loan.rating,
                    loan_tenure: loan.loan_tenure,
                    createdAt: moment(portfolio.createdAt).toDate(),
                    numberOfEmisOverdue: numberOfEmisOverdue,
                    portfolioName: startCase(portfolio.portfolio_name),
                    investmentInLoan: lenderInvestment.invested_amount,
                    totalNetAmountOverdue: totalNetAmountOverDue,
                    numberOfDaysDelayed: numberOfDaysDelayed,
                    outstandingPrincipal: outstandingPrincipal * lenderShare,
                    numberOfEmiReceived: receivedEmiMetric.noOfReceivedEmis,
                    netInterestEarned: netInterestEarned,
                    grossCashFlowReceived: receivedEmiMetric.grossCashFlowReceived * lenderShare,
                    lastPaymentDate: this.getLastPaymentDate(loan)
                });
            }
            this.getTotalSum(lenderDelayedLoans);
        }
        return lenderDelayedLoans;
    }

    private getTotalSum(lenderDelayedLoans: LenderDelayedLoan[]) {
        this.totalInvestmentInLoan = lenderDelayedLoans.map((lenderDelayedLoan) => lenderDelayedLoan.investmentInLoan)
            .reduce((previous, current) => previous + current, 0);
        this.totalGrossCashFlow = lenderDelayedLoans.map((lenderDelayedLoan) => lenderDelayedLoan.grossCashFlowReceived)
            .reduce((previous, current) => previous + current, 0);
        this.totalInterestEarned = lenderDelayedLoans.map((lenderDelayedLoan) => lenderDelayedLoan.netInterestEarned)
            .reduce((previous, current) => previous + current, 0);
        this.totalOutstandingPrincipal = lenderDelayedLoans.map((lenderDelayedLoan) => lenderDelayedLoan.outstandingPrincipal)
            .reduce((previous, current) => previous + current, 0);
        this.totalDelayedEmiAmount = lenderDelayedLoans.map((lenderDelayedLoan) => lenderDelayedLoan.totalNetAmountOverdue)
            .reduce((previous, current) => previous + current, 0);
    }

    private getNoOfEmiDelays(loan: Loan): number {
        let noOfDelays = 0;
        loan.emi_schedule.forEach(emi => {
            if ((emi.status === EmiStatus.PDCInitiated || emi.status === EmiStatus.PDCFailed) &&
                (moment().endOf('day').diff(moment(emi.due_on).endOf('day'), 'days') > this.configService.getEmiGracePeriodInDays()
                    || this.isAlreadyDelay(noOfDelays, emi.due_on))) {
                noOfDelays++;
            }
        });
        return noOfDelays;
    }

    private getNoOfDaysDelayed(outStandingDelay: EmiDelay, loan: Loan) {
        if (outStandingDelay && outStandingDelay.due_date) {
            return moment().endOf('day').diff(moment(outStandingDelay.due_date).endOf('day'), 'days');
        } else {
            let delayedDays = 0;
            loan.emi_schedule.forEach(emi => {
                const emiDelayInDays = moment().endOf('day').diff(moment(emi.due_on).endOf('day'), 'days');
                if ((emi.status === EmiStatus.PDCInitiated || emi.status === EmiStatus.PDCFailed) &&
                    emiDelayInDays > this.configService.getEmiGracePeriodInDays()) {
                    delayedDays = delayedDays > emiDelayInDays ? delayedDays : emiDelayInDays;
                }
            });
            return delayedDays;
        }
    }


    private async getReceivedEmiMetric(loan: Loan): Promise<{ noOfReceivedEmis: number, grossCashFlowReceived: number }> {
        let noOfReceivedEmis = 0;
        let grossCashFlowReceived = 0;
        loan.emi_schedule.forEach(emi => {
            if ((emi.status === EmiStatus.Received || emi.status === EmiStatus.Completed)
                && moment().isSameOrAfter(emi.due_on)) {
                const penalInterest = emi.penalInterest ? emi.penalInterest : 0;
                grossCashFlowReceived += emi.emi_amount + penalInterest;
                noOfReceivedEmis++;
            }
        });
        if (loan.prepayment_details) {
            loan.prepayment_details.details.forEach(payment => {
                if (payment.prepayment_status === PrepaymentStatus.PartpaymentCompleted || payment.prepayment_status === PrepaymentStatus.PrepaymentCompleted) {
                    grossCashFlowReceived += payment.prepayment_interest + payment.prepayment_principal;
                }
            });
        }
        if (isDate(loan.loanRestructuredOn)) {
            grossCashFlowReceived += await this.getUpfrontPaymentFromRestructure(loan);
        }
        return { noOfReceivedEmis, grossCashFlowReceived };
    }

    private getOutstandingPrincipal(loan: Loan) {
        const delayedEmis = loan.emi_schedule.filter(emi => !['emi_received', 'emi_completed'].includes(emi.status))
            .filter(emi => new Date(emi.due_on) < new Date());
        const firstDelayedEmi = delayedEmis.find(emi => !!emi);
        return firstDelayedEmi.balance_amount + firstDelayedEmi.principal_repayment;
    }

    private isAlreadyDelay(noOfDelays: number, due_on: Date) {
        return noOfDelays > 0 && moment().endOf('day').diff(moment(due_on).endOf('day'), 'days') > 0;
    }

    private getLastPaymentDate(loan: Loan): any {
        const lastPaidEmi = this.getLastPaidEmi(loan);
        const lastSuccessfulPrepayment = this.getLatestSuccessfulPrepayment(loan);
        if (!lastSuccessfulPrepayment) {
            if (!lastPaidEmi) {
                return '-';
            } else {
                return lastPaidEmi.paid_on;
            }
        } else {
            const latestPrepaymentDate = moment(lastSuccessfulPrepayment.payment_date);
            if (!lastPaidEmi || latestPrepaymentDate.isAfter(lastPaidEmi.paid_on)) {
                return latestPrepaymentDate.toDate();
            } else {
                return lastPaidEmi.paid_on;
            }
        }
    }

    private getLastPaidEmi(loan: Loan): Emi {
        const paidEmis = loan.emi_schedule.filter(emi => emi.status === EmiStatus.Received || emi.status === EmiStatus.Completed);
        return maxBy(paidEmis, schedule => schedule.paid_on);
    }

    public getLatestSuccessfulPrepayment(loan: Loan) {
        const completedPrepayments = loan.prepayment_details.details.filter((prepayment) => {
            return prepayment.prepayment_status === 'received' || prepayment.prepayment_status === 'partpayment_completed';
        });
        if (isEmpty(completedPrepayments)) {
            return null;
        }
        return maxBy(completedPrepayments, (prepayment) => prepayment.payment_date);
    }

    private async getUpfrontPaymentFromRestructure(loan: Loan): Promise<number> {
        let cashflow = 0;
        (await this.loanRestructureService.getByLoanId(loan._id))
            .map(restructure => {
                cashflow += restructure.upfrontPaymentPrincipal + restructure.upfrontPaymentInterest;
            });
        return cashflow;
    }
}
